import {
  guestsManagementAddGuestThunk,
  guestsManagementEditGuestThunk,
  guestsManagementRemoveGuestThunk,
  guestsManagementRemovePastGuestThunk,
  guestsManagementAddGuestFromGuardThunk,
  guestsManagementGuardSetCountToShowAction,
  guestsManagementGetAllPastGuestsByIdThunk,
  guestsManagementRemoveGuardPastGuestThunk,
  guestsManagementGetAllGuardPastGuestsThunk,
  guestsManagementAddManyGuestsFromFileThunk,
  guestsManagementRemoveGuardFutureGuestThunk,
  guestsManagementGetAllFutureGuestsByIdThunk,
  guestsManagementGetAllGuardFutureGuestsThunk,
  guestsManagementGuardSetFullNameFilterAction,
  guestsManagementGuardSetBuildingFilterAction,
  guestsManagementMyGuestsSetCountToShowAction,
  guestsManagementRefreshAllGuardPastGuestsThunk,
  guestsManagementGuardSetCompanyNameFilterAction,
  guestsManagementGuardSetArrivalDateFilterAction,
  guestsManagementMyGuestsSetFullNameFilterAction,
  guestsManagementRefreshAllGuardFutureGuestsThunk,
  guestsManagementMyGuestsSetArrivalDateFilterAction,
  guestsManagementSetLicenseNumberFilterAction,
  guestsManagementGuardSetLicenseNumberFilterAction,
  guestsManagementRepeatableGuestsSetFullNameFilterAction,
  guestsManagementAddRepeatableGuestThunk,
  guestsManagementUpdateRepeatableGuestThunk,
  guestsManagementRemoveRepeatableGuestThunk,
  guestsManagementGetAllRepeatableGuestsThunk,
  guestsManagementRepeatableGuestsSetCountToShowAction,
  guestsManagementGuardSetPhoneFilterAction,
  guestsManagementSetPhoneFilterAction,
  guestsManagementRepeatableGuestsSetSelectedIdsAction,
} from './guestsManagementActions';
import { useAppAction, useAppSelector } from '../hooks';
import { GuestsManagementState } from './guestsManagementTypes';
import { GUESTS_MANAGEMENT_NAMESPACE } from './guestsManagementReducer';

export function useGuestsManagementSelector<
  T extends (state: GuestsManagementState) => any
>(selector: T): ReturnType<T> {
  return useAppSelector((state) =>
    selector(state[GUESTS_MANAGEMENT_NAMESPACE])
  );
}

export const useGuestsManagementGetAllFutureGuests = () =>
  useGuestsManagementSelector((state) => {
    return state.futureGuests;
  });

export const useGuestsManagementGetAllPastGuests = () =>
  useGuestsManagementSelector((state) => {
    return state.pastGuests;
  });

export const useGuestById = (id: number) =>
  useGuestsManagementSelector((state) => {
    return state.futureGuests.find((guest) => guest.id === id);
  });

export const usePastGuestById = (id: number) =>
  useGuestsManagementSelector((state) => {
    return state.pastGuests.find((guest) => guest.id === id);
  });

export const useGuestsManagementGetAllGuardPastGuests = () =>
  useGuestsManagementSelector((state) => {
    return state.guardPastGuests;
  });

export const useGuestsManagementGetAllGuardFutureGuests = () =>
  useGuestsManagementSelector((state) => {
    return state.guardFutureGuests;
  });

export const useGuestsManagementGuardPagination = () =>
  useGuestsManagementSelector((state) => {
    return state.guardGuestPagination;
  });

export const useGuestsManagementGuardFilters = () =>
  useGuestsManagementSelector((state) => {
    return state.guardGuestFilters;
  });

export const useGuestsManagementMyGuestsPagination = () =>
  useGuestsManagementSelector((state) => {
    return state.pagination;
  });

export const useGuestsManagementMyGuestsFilters = () =>
  useGuestsManagementSelector((state) => {
    return state.guestFilters;
  });

export const useGuestsManagementRepeatableGuests = () =>
  useGuestsManagementSelector((state) => {
    return state.repeatableGuests.guests;
  });

export const useGuestsManagementRepeatableGuestsFilters = () =>
  useGuestsManagementSelector((state) => {
    return state.repeatableGuests.filters;
  });

export const useGuestsManagementRepeatableGuestsPagination = () =>
  useGuestsManagementSelector((state) => {
    return state.repeatableGuests.pagination;
  });

export const useGuestsManagementRepeatableGuestsSelectedIds = () =>
  useGuestsManagementSelector((state) => {
    return state.repeatableGuests.selectedIds;
  });

export const useSetGuardLicenseNumberFilter = () =>
  useAppAction(guestsManagementGuardSetLicenseNumberFilterAction);

export const useSetLicenseNumberFilter = () =>
  useAppAction(guestsManagementSetLicenseNumberFilterAction);

export const useGuestsManagementAddNewGuest = () =>
  useAppAction(guestsManagementAddGuestThunk);

export const useGuestsManagementAddNewGuestFromGuard = () =>
  useAppAction(guestsManagementAddGuestFromGuardThunk);

export const useGuestsManagementUpdateGuest = () =>
  useAppAction(guestsManagementEditGuestThunk);

export const useGuestsManagementRemoveGuest = () =>
  useAppAction(guestsManagementRemoveGuestThunk);

export const useGuestsManagementRemovePastGuest = () =>
  useAppAction(guestsManagementRemovePastGuestThunk);

export const useFetchAllPastGuests = () =>
  useAppAction(guestsManagementGetAllPastGuestsByIdThunk);

export const useGuestsManagementRemoveGuardPastGuest = () =>
  useAppAction(guestsManagementRemoveGuardPastGuestThunk);

export const useFetchAllPastGuardGuests = () =>
  useAppAction(guestsManagementGetAllGuardPastGuestsThunk);

export const useGuestsManagementRemoveGuardFutureGuest = () =>
  useAppAction(guestsManagementRemoveGuardFutureGuestThunk);

export const useFetchAllFutureGuardGuests = () =>
  useAppAction(guestsManagementGetAllGuardFutureGuestsThunk);

export const useRefreshFutureGuardGuests = () =>
  useAppAction(guestsManagementRefreshAllGuardFutureGuestsThunk);

export const useRefreshPastGuardGuests = () =>
  useAppAction(guestsManagementRefreshAllGuardPastGuestsThunk);

export const useFetchAllFutureGuests = () =>
  useAppAction(guestsManagementGetAllFutureGuestsByIdThunk);

export const useSetCountToShowPagination = () =>
  useAppAction(guestsManagementGuardSetCountToShowAction);

export const useSetFullNameFilter = () =>
  useAppAction(guestsManagementGuardSetFullNameFilterAction);

export const useSetCompanyNameFilter = () =>
  useAppAction(guestsManagementGuardSetCompanyNameFilterAction);

export const useSetPhoneFilter = () =>
  useAppAction(guestsManagementSetPhoneFilterAction);

export const useSetGuardPhoneFilter = () =>
  useAppAction(guestsManagementGuardSetPhoneFilterAction);

export const useSetBuildingFilter = () =>
  useAppAction(guestsManagementGuardSetBuildingFilterAction);

export const useSetGuardArrivalDateFilter = () =>
  useAppAction(guestsManagementGuardSetArrivalDateFilterAction);

export const useGuestsManagementAddManyGuestsFromFile = () =>
  useAppAction(guestsManagementAddManyGuestsFromFileThunk);

export const useSetMyGuestsCountToShowPagination = () =>
  useAppAction(guestsManagementMyGuestsSetCountToShowAction);

export const useSetMyGuestsFullNameFilter = () =>
  useAppAction(guestsManagementMyGuestsSetFullNameFilterAction);

export const useSetMyGuestsArrivalDateFilter = () =>
  useAppAction(guestsManagementMyGuestsSetArrivalDateFilterAction);

export const useSetRepeatableGuestsFullNameFilter = () =>
  useAppAction(guestsManagementRepeatableGuestsSetFullNameFilterAction);

export const useSetRepeatableGuestsSetCountToShow = () =>
  useAppAction(guestsManagementRepeatableGuestsSetCountToShowAction);

export const useAddRepeatableGuest = () =>
  useAppAction(guestsManagementAddRepeatableGuestThunk);

export const useUpdateRepeatableGuest = () =>
  useAppAction(guestsManagementUpdateRepeatableGuestThunk);

export const useRemoveRepeatableGuest = () =>
  useAppAction(guestsManagementRemoveRepeatableGuestThunk);

export const useFetchRepeatableGuest = () =>
  useAppAction(guestsManagementGetAllRepeatableGuestsThunk);

export const useSetRepeatableGuestsSelectedIds = () =>
  useAppAction(guestsManagementRepeatableGuestsSetSelectedIdsAction);
